import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  MessageSquare,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  X,
  Send,
  Eye,
  UserCheck,
  Settings,
  Search
} from 'lucide-react';
import { getApiBaseUrl } from '../../config/apiBase';
import UniversalSearchInput from '../../components/common/UniversalSearchInput';

const AdminSupportDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('open');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    open: 0,
    assigned: 0,
    resolved: 0,
    closed: 0
  });

  useEffect(() => {
    fetchConversations();
    fetchStats();
    
    // Poll for updates every 30 seconds
    const interval = setInterval(() => {
      fetchConversations();
      fetchStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const status = activeTab === 'all' ? '' : activeTab.toUpperCase();
      const response = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/admin/conversations?status=${status}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const statuses = ['open', 'assigned', 'resolved', 'closed'];
      const statsData = {};
      
      for (const status of statuses) {
        const response = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/conversations?status=${status.toUpperCase()}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          statsData[status] = data.total || 0;
        }
      }
      
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const assignConversation = async () => {
    if (!selectedConversation || !selectedAgent) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/conversations/${selectedConversation.id}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          agent_id: parseInt(selectedAgent)
        })
      });
      
      if (response.ok) {
        toast.success('Conversation assigned successfully');
        setShowAssignModal(false);
        setSelectedAgent('');
        fetchConversations();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to assign conversation');
      }
    } catch (error) {
      console.error('Failed to assign conversation:', error);
      toast.error('Failed to assign conversation');
    }
  };

  const closeConversation = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/conversations/${conversationId}/close`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        toast.success('Conversation closed successfully');
        fetchConversations();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to close conversation');
      }
    } catch (error) {
      console.error('Failed to close conversation:', error);
      toast.error('Failed to close conversation');
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedConversation) return;
    
    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/conversations/${selectedConversation.id}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: messageInput.trim()
        })
      });
      
      if (response.ok) {
        setMessageInput('');
        // Refresh conversation details
        const detailResponse = await fetch(`${getApiBaseUrl().replace(':8080', ':8081')}/conversations/${selectedConversation.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (detailResponse.ok) {
          const conversation = await detailResponse.json();
          setSelectedConversation(conversation);
        }
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const viewConversation = (conversation) => {
    setSelectedConversation(conversation);
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Filter conversations based on search term
  const filteredConversations = conversations.filter((conv) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (conv.subject && conv.subject.toLowerCase().includes(searchLower)) ||
      (conv.conversation_uuid && conv.conversation_uuid.toLowerCase().includes(searchLower)) ||
      (`User #${conv.user_id}`.toLowerCase().includes(searchLower)) ||
      (conv.status && conv.status.toLowerCase().includes(searchLower)) ||
      (conv.priority && conv.priority.toLowerCase().includes(searchLower))
    );
  });

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'LOW': return 'bg-gray-100 text-gray-800';
      case 'NORMAL': return 'bg-blue-100 text-blue-800';
      case 'HIGH': return 'bg-orange-100 text-orange-800';
      case 'CRITICAL': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'bg-yellow-100 text-yellow-800';
      case 'ASSIGNED': return 'bg-blue-100 text-blue-800';
      case 'RESOLVED': return 'bg-green-100 text-green-800';
      case 'CLOSED': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const tabs = [
    { id: 'open', label: 'Open Conversations', icon: AlertCircle, count: stats.open },
    { id: 'assigned', label: 'Assigned', icon: UserCheck, count: stats.assigned },
    { id: 'unassigned', label: 'Unassigned Queue', icon: Users, count: stats.open },
    { id: 'resolved', label: 'Resolved', icon: CheckCircle, count: stats.resolved },
    { id: 'closed', label: 'Closed', icon: X, count: stats.closed },
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Live Support Dashboard</h1>
        <p className="text-gray-600">Manage customer support conversations</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Open</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.open}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Assigned</p>
              <p className="text-2xl font-bold text-blue-600">{stats.assigned}</p>
            </div>
            <UserCheck className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Resolved</p>
              <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Closed</p>
              <p className="text-2xl font-bold text-gray-600">{stats.closed}</p>
            </div>
            <X className="w-8 h-8 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="bg-gray-100 text-gray-900 px-2 py-1 text-xs rounded-full">
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Search Bar */}
        <div className="px-6 pb-4">
          <div className="max-w-md">
            <UniversalSearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search conversations by subject, user, or ID..."
            />
          </div>
        </div>

        {/* Conversations List */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">
              Loading conversations...
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {searchTerm ? 'No conversations match your search' : 'No conversations found'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Conversation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredConversations.map((conv) => (
                    <tr key={conv.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {conv.subject || 'No subject'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {conv.conversation_uuid}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          User #{conv.user_id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(conv.status)}`}>
                          {conv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(conv.priority)}`}>
                          {conv.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {conv.assignment?.agent?.role || 'Unassigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(conv.updated_at)} {formatTime(conv.updated_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => viewConversation(conv)}
                          className="text-indigo-600 hover:text-indigo-900 mr-3"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {conv.status === 'OPEN' && (
                          <button
                            onClick={() => {
                              setSelectedConversation(conv);
                              setShowAssignModal(true);
                            }}
                            className="text-green-600 hover:text-green-900 mr-3"
                          >
                            <UserCheck className="w-4 h-4" />
                          </button>
                        )}
                        {(conv.status === 'OPEN' || conv.status === 'ASSIGNED') && (
                          <button
                            onClick={() => closeConversation(conv.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Conversation Details Modal */}
      {selectedConversation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold">
                    {selectedConversation.subject || 'Support Conversation'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    ID: {selectedConversation.conversation_uuid}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="flex flex-col h-[600px]">
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {selectedConversation.messages?.map((message, index) => (
                  <div
                    key={index}
                    className={`flex ${message.sender_type === 'USER' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-md px-4 py-2 rounded-lg ${
                        message.sender_type === 'USER'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="text-sm">{message.message}</p>
                      <p className={`text-xs mt-1 ${
                        message.sender_type === 'USER' ? 'text-indigo-200' : 'text-gray-500'
                      }`}>
                        {message.sender_type} • {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Message Input */}
              <div className="p-6 border-t border-gray-200">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type your reply..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled={sending}
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!messageInput.trim() || sending}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Assign Conversation</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Agent
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => setSelectedAgent(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Choose an agent...</option>
                <option value="1">Support Agent 1</option>
                <option value="2">Support Agent 2</option>
                <option value="3">Admin Agent</option>
              </select>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedAgent('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={assignConversation}
                disabled={!selectedAgent}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSupportDashboard;
